import React, { useState, useContext } from "react";
import useSWR from "swr";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { NavigationContext } from "navigation-react";
import LoaderAnimation from "./LoaderAnimationLarge";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes" });

// Admin credentials from secure storage (for authentication)
const username = ls.get("username");
const userpw = ls.get("userpw");
const usercsrf_token = ls.get("usercsrf_token");

const fetcher = (url) =>
  fetch(url, {
    credentials: "same-origin",
    mode: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Basic ${btoa(`${username}:${userpw}`)}`,
      "X-CSRF-Token": usercsrf_token,
    },
  }).then((res) => res.json());

const AddUserRegister = () => {
  const { stateNavigator } = useContext(NavigationContext);
  const [loading, setLoading] = useState(false);
  const [usermail, setusermail] = useState("");
  const [username, setusername] = useState(""); // For the new user
  const [userpassword, setuserpassword] = useState("");
  const [userconfirmpassword, setuserconfirmpassword] = useState("");
  const [userfullname, setuserfullname] = useState("");
  const [userphonenumber, setuserphonenumber] = useState("");
  const [userspecialty, setuserspecialty] = useState("");
  const [usersRoles, setusersRoles] = useState("");
  const [file, setFile] = useState(null);

  const { data: usersRoleData, error } = useSWR(
    "/user-register-role-api",
    fetcher
  );

  // Deduplicate user roles
  const uniqueRoles = usersRoleData
    ? usersRoleData.filter(
        (role, index, self) =>
          index ===
          self.findIndex((t) => t.roles_target_id_1 === role.roles_target_id_1)
      )
    : [];

  if (error) {
    return (
      <LoaderAnimation loadingTxt="Failed to load user roles. Please try again later." />
    );
  }

  // Handle file selection
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  // Upload file to Drupal and return the file ID
  const uploadFile = async (userId) => {
    if (!file || !usercsrf_token) {
      console.warn("No file or CSRF token available.");
      return null;
    }

    try {
      const response = await fetch(
        `/file/upload/user/user/field_signature?_format=json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/octet-stream",
            "Content-Disposition": `file; filename="${file.name}"`,
            Authorization: `Basic ${btoa(`${username}:${userpw}`)}`,
            "X-CSRF-Token": usercsrf_token,
          },
          body: file,
        }
      );

      if (!response.ok) {
        throw new Error(`File upload failed: ${response.statusText}`);
      }

      const data = await response.json();
      const fid = data.fid[0].value;
      console.log("File uploaded successfully, FID:", fid);

      // Update the newly created user with the file
      await updateUserWithSignatureImage(userId, fid);
      return fid;
    } catch (error) {
      console.error("File upload failed:", error);
      return null;
    }
  };

  // Update the user's field_signature
  const updateUserWithSignatureImage = async (userId, fid) => {
    const userData = {
      field_signature: [{ target_id: fid }],
    };

    try {
      const response = await fetch(`/user/${userId}?_format=json`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${btoa(`${username}:${userpw}`)}`,
          "X-CSRF-Token": usercsrf_token,
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        throw new Error(`User update failed: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("User updated successfully with signature:", data);
    } catch (error) {
      console.error("User update failed:", error);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (userpassword !== userconfirmpassword) {
      alert("Your password does not match. Please check and try again");
      setLoading(false);
      return;
    }

    const dataFields = {
      field_doctor_referer_full_name: [{ value: userfullname }],
      field_doctor_ref_contact_number: [{ value: userphonenumber }],
      field_specialty: [{ value: userspecialty }],
      mail: [{ value: usermail }],
      roles: [{ target_id: usersRoles }],
      status: [{ value: "1" }],
      name: [{ value: username }],
      pass: [{ value: userpassword }],
      timezone: [{ value: "Africa/Accra" }],
    };

    try {
      const response = await fetch(`/entity/user?_format=json`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Basic ${btoa(`${username}:${userpw}`)}`,
          "X-CSRF-Token": usercsrf_token,
        },
        body: JSON.stringify(dataFields),
      });

      if (response.ok) {
        const newUserData = await response.json();
        const newUserId = newUserData.uid[0].value;
        console.log("User created successfully, UID:", newUserId);

        // If a file is selected, upload it and attach it to the new user
        if (file) {
          await uploadFile(newUserId);
        }

        setLoading(false);
        stateNavigator.navigate("userregister");
        alert("User added successfully!");
      } else {
        const errorData = await response.json();
        setLoading(false);
        alert(`Error adding user: ${errorData.message || "Unknown error"}`);
        console.error("Error adding user:", errorData);
      }
    } catch (error) {
      setLoading(false);
      alert("Network error. Please check your connection.");
      console.error("Network error:", error);
    }
  };

  if (loading) {
    return <LoaderAnimation />;
  }

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div className="add-patient-register">
          <TopActionMenus
            AddTitle="Add new user"
            backStateKey="userregister"
            backIcon={faArrowLeft}
            backLable="Back"
          />
          <form onSubmit={handleSubmit} className="patient-form">
            <div className="form-group">
              <label htmlFor="userfullname">Full Name</label>
              <input
                type="text"
                placeholder="Enter user full name"
                value={userfullname}
                onChange={(e) => setuserfullname(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="userphonenumber">Contact Number</label>
              <input
                type="tel"
                placeholder="Enter user contact number"
                value={userphonenumber}
                onChange={(e) => setuserphonenumber(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="userspecialty">Specialty</label>
              <input
                type="text"
                placeholder="Enter specialty"
                value={userspecialty}
                onChange={(e) => setuserspecialty(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="usermail">Email Address</label>
              <input
                type="email"
                placeholder="Enter user email address"
                value={usermail}
                onChange={(e) => setusermail(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="usersRoles">User Role</label>
              <select
                value={usersRoles}
                onChange={(e) => setusersRoles(e.target.value)}
                required
              >
                <option value="">Select User Role</option>
                {uniqueRoles.map((role) => (
                  <option
                    key={role.roles_target_id_1}
                    value={role.roles_target_id_1}
                  >
                    {role.roles_target_id}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setusername(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="userpassword">Password</label>
              <input
                type="password"
                placeholder="Enter password"
                value={userpassword}
                onChange={(e) => setuserpassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="userconfirmpassword">Confirm Password</label>
              <input
                type="password"
                placeholder="Confirm password"
                value={userconfirmpassword}
                onChange={(e) => setuserconfirmpassword(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="usersignature">Signature</label>
              <input
                type="file"
                accept="image/png, image/jpeg"
                onChange={handleFileChange}
              />
            </div>
            <div className="submitGroup">
              <button type="submit" disabled={loading}>
                Submit
              </button>
            </div>
          </form>
        </div>
      </div>
      <DashboardContentFooter />
    </div>
  );
};

export default AddUserRegister;
